import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  BuyMasterPayload,
  InvestPaymentMethodTypes,
  OrderPayload,
  OrderOTPTriggerPayload,
  PaymentPageType,
  OrderOtpState,
  InvestPaymentMethodType
} from '../../types/mf-execution.types'
import {
  OTPVerificationState,
  OTPVerificationHandlers,
  OTPVerificationFailedCase as OTPFailedCase
} from '../../types/otp.types'
import { BuyContext } from '../contexts/BuyContext'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createOrder, fetchUserInvestPaymentMethodsV2, triggerOrderOTP } from '../../repository/mfExecution'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { manualDelay } from '../../utils/utils'
import { OTP_VERIFICATION_DELAY } from '../../constants'
import { useInstalledUpiIntents } from '../../hooks/mf-execution/useInstalledUpiIntents'
import { buildOrderOtpPaymentFields } from '../../utils/mf-execution/paymentMethodSelection'
import { isUpiIntentsFlowEnabled } from '../../utils/mf-execution/upiIntentsFeature'
import { useMFExecutionContext } from '../contexts/MFExecutionContext'
import {
  AmountAdjustmentReason,
  getEffectiveMaxAmount,
  normalizeAllocationAmount,
} from '../../utils/mf-execution/paymentAmount'

interface BuyProviderProps {
  children: ReactNode
}

const OTP_VERIFICATION_LIMIT = 300

const BuyProvider: React.FC<BuyProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { mfExecutionData } = useMFExecutionContext()
  const [isBuyOTPVerified, setIsBuyOTPVerified] = useState(false)
  const [buyMasterPayload, setBuyMasterPayload] = useState<BuyMasterPayload>({
    paymentMethodId: '',
    paymentMethodType: InvestPaymentMethodTypes.MANDATE,
    transactionType: 'BUY',
    isin: '',
    amount: 0
  })
  const [amount, setAmount] = useState(0)
  const [initialShownAmount, setInitialShownAmount] = useState<number | null>(null)
  const [fromAdvisory, setFromAdvisory] = useState(() => searchParams.get('fromAdvisory') === 'true')
  const [fromApp, setFromApp] = useState(() => searchParams.get('from') === 'APP')
  const [amountAdjustmentReason, setAmountAdjustmentReason] = useState(AmountAdjustmentReason.NONE)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<InvestPaymentMethodType | null>(null)
  const [otpVerificationCount, setOtpVerificationCount] = useState(0)
  const [otpReferenceId, setOtpReferenceId] = useState('')
  const [isOTPVerificationLimitExceeded, setIsOTPVerificationLimitExceeded] = useState(false)
  const [OTPVerificationFailedCase, setOTPVerificationFailedCase] = useState<OTPFailedCase | null>(null)
  const [otpState, setOtpState] = useState<OrderOtpState | null>(null)

  const { data: paymentDetails, isPending: isPaymentDetailsPending, refetch: refetchPaymentMethods } = useQuery({
    queryKey: ['user-invest-payment-details'],
    queryFn: fetchUserInvestPaymentMethodsV2
  })

  // Opening amount only. Keypad edits leave this as-is.
  const rememberInitialShownAmount = (shownAmount: number) => {
    if (shownAmount <= 0) return
    setInitialShownAmount((current) => current ?? shownAmount)
  }

  // Priority: ?amount= (forced, allocation-normalized) > leave at 0.
  // Strip only amount after apply; omit searchParams from deps so clearing does not re-run seed.
  useEffect(() => {
    const amountFromParams = parseFloat(searchParams.get('amount') ?? '0')
    const amountFromEntry =
      Number.isFinite(amountFromParams) && amountFromParams > 0 ? amountFromParams : 0
    const fromAdvisoryEntry = searchParams.get('fromAdvisory') === 'true'
    const fromAppEntry = searchParams.get('from') === 'APP'
    setFromAdvisory(fromAdvisoryEntry)
    setFromApp(fromAppEntry)

    if (amountFromEntry <= 0) return
    if (fromAdvisoryEntry && !mfExecutionData?.buy) return
    if (fromAdvisoryEntry && isPaymentDetailsPending) return

    if (fromAdvisoryEntry) {
      const { amount: normalizedAmount, adjustment } = normalizeAllocationAmount(amountFromEntry, {
        minAmount: mfExecutionData?.buy?.minAmount ?? 0,
        maxAmount: getEffectiveMaxAmount(
          mfExecutionData?.buy?.maxAmount ?? 0,
          paymentDetails?.paymentMethods
        ),
        stepper: mfExecutionData?.buy?.stepper,
      })
      setAmount(normalizedAmount)
      rememberInitialShownAmount(normalizedAmount)
      setAmountAdjustmentReason(adjustment)
    } else {
      setAmount(amountFromEntry)
      rememberInitialShownAmount(amountFromEntry)
      setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
    }

    if (searchParams.has('amount')) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('amount')
      setSearchParams(nextParams, { replace: true })
    }
  }, [mfExecutionData?.buy, isPaymentDetailsPending, paymentDetails?.paymentMethods])

  const handleAmountChange = (amountParam: number) => {
    setAmount(amountParam)
  }

  const clearAmountAdjustmentReason = useCallback(() => {
    setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
  }, [])

  const upiIntentsEnabled = isUpiIntentsFlowEnabled()
  const { installedUpiApps, requestUpiIntents } = useInstalledUpiIntents({
    enabled: upiIntentsEnabled,
    requestOnMount: upiIntentsEnabled,
  })

  const paymentMethods = paymentDetails?.paymentMethods
  const bankAccountDetails = paymentDetails?.bankAccountDetails
  const defaultPaymentMethodDetails = paymentDetails?.defaultPaymentMethodDetails
  const upiIntentMaximumAllowedAmount = paymentDetails?.upiIntentMaximumAllowedAmount ?? 0

  const { mutate: triggerOTPMutate } = useMutation({
    mutationFn: (payload: OrderOTPTriggerPayload) => triggerOrderOTP(payload),
    onSuccess: (data) => {
      console.log('OTP triggered successfully', data)
      setOtpReferenceId(data?.otpReferenceId)
    },
    onError: (error) => {
      console.error('OTP trigger error:', error)
    }
  })

  // This same API is used for OTP Verification and Order Creation
  const { mutate: createOrderMutate, isPending: isOTPVerificationLoading } = useMutation({
    mutationFn: (payload: OrderPayload) => createOrder(payload),
    onSuccess: async (data) => {
      setOtpState(data?.otpState ?? OrderOtpState.UNKNOWN)
      if (data?.isOtpVerified) {
        setIsBuyOTPVerified(true)
        await manualDelay(OTP_VERIFICATION_DELAY)
        if (buyMasterPayload.paymentMethodType === InvestPaymentMethodTypes.NET_BANKING) {
          // Go To NetBanking Loading
          navigate(`${PaymentPageType.BUY}/net-banking/link-waiting?orderId=${data?.orderId}`)
        } else if (
          buyMasterPayload.paymentMethodType === InvestPaymentMethodTypes.UPI ||
          buyMasterPayload.paymentMethodType === InvestPaymentMethodTypes.UPI_INTENT
        ) {
          const displayAccNumber = selectedPaymentMethod?.displayAccNumber || ''
          navigate(
            `${PaymentPageType.BUY}/upi/payment-status?orderId=${data?.orderId}&displayAccNumber=${encodeURIComponent(displayAccNumber)}`
          )
        } else if (buyMasterPayload.paymentMethodType === InvestPaymentMethodTypes.MANDATE) {
          navigate(`${PaymentPageType.BUY}/order-success`)
        } else {
          navigate(`${PaymentPageType.BUY}/order-success`)
        }
      } else {
        // Handle different OTP states
        if (data?.otpState === OrderOtpState.MAXIMUM_LIMIT_REACHED) {
          setIsOTPVerificationLimitExceeded(true)
        } else if (data?.otpState === OrderOtpState.INVALID_OTP) {
          setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
        } else if (data?.otpState === OrderOtpState.MAXIMUM_INCORRECT_RESPONSE) {
          setIsOTPVerificationLimitExceeded(true)
        } else {
          setOTPVerificationFailedCase(OTPFailedCase.OTP_VERIFICATION_FAILED)
        }
      }
    },
    onError: () => {
      setOtpState(OrderOtpState.UNKNOWN)
      if (buyMasterPayload.paymentMethodType === InvestPaymentMethodTypes.MANDATE) {
        navigate(`${PaymentPageType.BUY}/payment-failed`)
      } else {
        setOTPVerificationFailedCase(OTPFailedCase.OTP_VERIFICATION_FAILED)
      }
    }
  })

  const resetOTPVerificationState = () => {
    setIsOTPVerificationLimitExceeded(false)
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(0)
    setIsBuyOTPVerified(false)
    setOtpState(null)
  }

  const handleOTPTrigger = (payload: BuyMasterPayload) => {
    resetOTPVerificationState()
    setBuyMasterPayload(payload)
    triggerOTPMutate(payload)
  }

  const handlePaymentMethodChange = (paymentMethod: InvestPaymentMethodType | null) => {
    setSelectedPaymentMethod(paymentMethod)
  }

  const handleBuyMasterPayload = (payload: BuyMasterPayload) => {
    setBuyMasterPayload(payload)
  }

  const isOTPValid = (otp: string) => {
    return otp.length === 6 && /^\d{6}$/.test(otp)
  }

  const handleBuyOTPVerification = async (otp: string) => {
    if (!isOTPValid(otp)) {
      setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
      return
    }
    if (otpVerificationCount >= OTP_VERIFICATION_LIMIT) {
      setIsOTPVerificationLimitExceeded(true)
      return
    }
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(otpVerificationCount + 1)
    createOrderMutate({
      otp: otp,
      otpReferenceId: otpReferenceId,
      orderDetails: buildBuyOrderDetails(buyMasterPayload, selectedPaymentMethod)
    })
  }

  const handleBuyResendOTP = () => {
    triggerOTPMutate(buyMasterPayload)
  }

  const otpVerificationState: OTPVerificationState = {
    isOTPVerified: isBuyOTPVerified,
    isOTPVerificationLoading,
    isOTPVerificationLimitExceeded,
    OTPVerificationFailedCase
  }

  const otpVerificationHandlers: OTPVerificationHandlers = {
    handleOTPVerification: handleBuyOTPVerification,
    handleResendOTP: handleBuyResendOTP
  }

  return (
    <BuyContext.Provider
      value={{
        buyMasterPayload,
        handleBuyMasterPayload,
        isBuyOTPVerified,
        handleBuyOTPVerification,
        otpVerificationState,
        otpVerificationHandlers,
        paymentMethods,
        bankAccountDetails,
        defaultPaymentMethodDetails,
        upiIntentMaximumAllowedAmount,
        selectedPaymentMethod,
        handlePaymentMethodChange,
        amount,
        initialShownAmount,
        handleAmountChange,
        fromAdvisory,
        fromApp,
        amountAdjustmentReason,
        clearAmountAdjustmentReason,
        handleOTPTrigger,
        handleRefetchPaymentMethods: refetchPaymentMethods,
        otpState,
        installedUpiApps,
        requestUpiIntents
      }}
    >
      {children}
    </BuyContext.Provider>
  )
}

export function buildBuyOrderDetails(
  buyMasterPayload: BuyMasterPayload,
  selectedPaymentMethod: InvestPaymentMethodType | null
): OrderOTPTriggerPayload {
  const otpPaymentFields = buildOrderOtpPaymentFields(
    {
      type: buyMasterPayload?.paymentMethodType ?? selectedPaymentMethod?.type,
      upiPackageName: selectedPaymentMethod?.upiPackageName ?? buyMasterPayload?.upiPackageName,
    },
    isUpiIntentsFlowEnabled()
  )

  return {
    paymentMethodId: buyMasterPayload?.paymentMethodId ?? null,
    paymentMethodType: otpPaymentFields.paymentMethodType,
    transactionType: buyMasterPayload?.transactionType,
    isin: buyMasterPayload?.isin ?? '',
    amount: buyMasterPayload?.amount ?? 0,
    ...(otpPaymentFields.upiPackageName ? { upiPackageName: otpPaymentFields.upiPackageName } : {}),
  }
}

export default BuyProvider
